import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, Zap, Landmark,
  ShieldCheck, Sparkles, Info, Clock, XCircle, Receipt, RefreshCw,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useVenue } from '../../contexts/VenueContext';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

const SEPA_FEE = 0.80;

function formatRelativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

const cardStyle = {
  ...hostCardStyle,
  width: 'calc(100% - 32px)',
  maxWidth: 360,
  padding: '18px 18px',
  boxSizing: 'border-box',
};

export default function VenuePayoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useBackTarget(location.state?.from || undefined);
  const { activeVenueAppId } = useVenue();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const getToken = useCallback(() => localStorage.getItem('token'), []);

  const fetchHistory = useCallback(async () => {
    if (!activeVenueAppId) return;
    try {
      const res = await axios.get(`${API}/venue/payout-history/${activeVenueAppId}?limit=10`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setHistory(res.data);
    } catch (err) {
      console.error('payout-history fetch failed', err);
      setHistory(null);
    }
  }, [activeVenueAppId, getToken]);

  const fetchStatus = useCallback(async () => {
    if (!activeVenueAppId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/venue/payout-method/${activeVenueAppId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('payout-method fetch failed', err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [activeVenueAppId, getToken]);

  const fetchAuditLog = useCallback(async () => {
    if (!activeVenueAppId) return;
    try {
      const res = await axios.get(`${API}/venue/payout-method/${activeVenueAppId}/audit-log?limit=20`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setAuditEntries(res.data?.entries || []);
    } catch (err) {
      console.error('payout audit-log fetch failed', err);
      setAuditEntries([]);
    }
  }, [activeVenueAppId, getToken]);

  useEffect(() => { fetchStatus(); fetchHistory(); fetchAuditLog(); }, [fetchStatus, fetchHistory, fetchAuditLog]);

  const handleRetryPending = useCallback(async () => {
    if (!activeVenueAppId || retrying) return;
    setRetrying(true);
    try {
      const res = await axios.post(
        `${API}/venue/payout-history/${activeVenueAppId}/retry`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      const { attempted = 0, executed = 0, still_pending: stillPending = 0, failed = 0 } = res.data || {};
      if (attempted === 0) {
        toast.info('No pending payouts to retry');
      } else if (executed === attempted) {
        toast.success(`${executed} payout${executed === 1 ? '' : 's'} sent`);
      } else if (executed > 0) {
        toast.success(`${executed} sent · ${stillPending} still waiting · ${failed} failed`);
      } else if (stillPending > 0) {
        toast.warning(`Still ${stillPending} pending — fund your Viva wallet and try again`);
      } else {
        toast.error(`${failed} retry attempt${failed === 1 ? '' : 's'} failed`);
      }
      await fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }, [activeVenueAppId, retrying, getToken, fetchHistory]);

  const handleDisconnect = useCallback(async () => {
    if (!activeVenueAppId) return;
    setDisconnectConfirmOpen(false);
    setDisconnecting(true);
    try {
      await axios.delete(`${API}/venue/payout-method/${activeVenueAppId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      toast.success('Payout method disconnected');
      await fetchStatus();
      await fetchAuditLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  }, [activeVenueAppId, fetchStatus, fetchAuditLog, getToken]);

  const method = data?.payout_method || null;
  const isOnHold = data?.is_on_hold !== false;
  const ibanMasked = data?.iban_masked || '';
  const vivaIbanMasked = data?.viva_iban_masked || '';

  const statusPill = useMemo(() => {
    if (isOnHold) {
      return (
        <div data-testid="venue-payout-onhold-banner" style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '12px 14px', borderRadius: 14,
          background: 'rgba(220, 38, 38, 0.08)', border: '1px solid rgba(220, 38, 38, 0.2)',
          marginBottom: 14,
        }}>
          <AlertTriangle size={20} color="#DC2626" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--status-danger-text)', margin: 0 }}>
              Your venue is on hold
            </p>
            <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: '4px 0 0', lineHeight: 1.25 }}>
              You're hidden from the guest map until you connect a payout method below.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div data-testid="venue-payout-connected-banner" style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 14px', borderRadius: 14,
        background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.2)',
        marginBottom: 14,
      }}>
        <CheckCircle2 size={20} color="#16A34A" style={{ flexShrink: 0 }} />
        <div>
          <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--status-success-text)', margin: 0 }}>
            Payouts active{method === 'viva_wallet' ? ' · Viva Wallet' : ' · IBAN (SEPA)'}
          </p>
          <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: '2px 0 0', lineHeight: 1.2 }}>
            {method === 'iban'
              ? `€${SEPA_FEE.toFixed(2)} deducted per transaction · Arrives in 1–2 business days`
              : 'No extra fees · Instant payouts'}
          </p>
        </div>
      </div>
    );
  }, [isOnHold, method]);

  // Venue staff cannot manage payout methods — show access-restricted screen.
  // NOTE: All hooks must be called before this early return.
  const cachedUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const userRole = cachedUser?.role;
  if (userRole === 'staff') {
    return (
      <div className="app-shell" data-testid="venue-payout-page" style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <AlertTriangle size={40} color="var(--status-danger-text)" style={{ marginBottom: 16 }} />
        <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', textAlign: 'center', marginBottom: 8 }}>Access restricted</p>
        <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', textAlign: 'center', maxWidth: 280, lineHeight: 1.4 }}>Payout settings are only available to venue owners.</p>
        <button onClick={goBack} style={{ marginTop: 24, background: 'none', border: 'none', color: 'var(--accent-purple)', fontFamily: font, fontSize: 16, cursor: 'pointer', textDecoration: 'underline' }}>Go back</button>
      </div>
    );
  }

  if (loading) return null;

  return (
    <div className="app-shell" data-testid="venue-payout-page" style={{
      minHeight: '100vh', backgroundColor: 'var(--app-bg)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40,
    }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-start', padding: '16px 20px 0' }}>
        <button data-testid="venue-payout-back-button" onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <ChevronLeft size={30} color="var(--text)" strokeWidth={2} />
        </button>
      </div>

      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-payout-shared-logo" />
      </div>
      <h2 style={{ fontFamily: font, fontSize: 28, fontWeight: 700, color: 'var(--text)', marginTop: 8, marginBottom: 6 }}>
        Payouts
      </h2>
      <p style={{ fontFamily: font, fontSize: 15, color: 'var(--text-2)', textAlign: 'center', maxWidth: 320, margin: '0 0 18px', lineHeight: 1.3 }}>
        Choose how you want to receive your money. Funds move <strong>instantly</strong> on every transaction — we never hold them.
      </p>

      <div style={{ width: 'calc(100% - 32px)', maxWidth: 360 }}>{statusPill}</div>

      {/* Option 1: Viva Wallet (recommended) */}
      <button
        data-testid="venue-payout-option-viva"
        onClick={() => {
          if (method === 'iban') {
            toast.message('Disconnect your IBAN first to switch to Viva Wallet.', { icon: 'i' });
            return;
          }
          navigate('/venue/settings/payouts/connect-viva', { state: { previousMethod: method } });
        }}
        disabled={method === 'iban'}
        style={{
          ...cardStyle,
          marginTop: 6,
          padding: '16px 16px',
          textAlign: 'left',
          border: method === 'viva_wallet' ? '2px solid #16A34A' : '1px solid var(--card-border)',
          background: 'var(--surface)',
          cursor: method === 'iban' ? 'not-allowed' : 'pointer',
          opacity: method === 'iban' ? 0.78 : 1,
          position: 'relative',
        }}
      >
        {method !== 'viva_wallet' && method !== 'iban' && (
          <span style={{
            position: 'absolute', top: -10, right: 12, padding: '3px 10px',
            background: '#16A34A', color: '#FFF', fontFamily: font, fontSize: 12, fontWeight: 700,
            borderRadius: 999, letterSpacing: 0.3,
          }}>
            RECOMMENDED
          </span>
        )}
        {method === 'iban' && (
          <span data-testid="venue-payout-viva-switch-hint" style={{
            position: 'absolute', top: -10, right: 12, padding: '3px 10px',
            background: '#16A34A', color: '#FFF', fontFamily: font, fontSize: 11, fontWeight: 700,
            borderRadius: 999, letterSpacing: 0.3,
          }}>
            SAVE €{SEPA_FEE.toFixed(2)} / TRANSACTION
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'rgba(34, 197, 94, 0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Zap size={24} color="#16A34A" strokeWidth={2.4} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <p style={{ fontFamily: font, fontSize: 19, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                Viva Wallet
              </p>
              {method === 'viva_wallet' && (
                <CheckCircle2 size={18} color="#16A34A" data-testid="venue-payout-viva-active-tick" />
              )}
            </div>
            <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: '2px 0 0', lineHeight: 1.2 }}>
              Instant · No extra fee per transaction
            </p>
            {method === 'iban' && (
              <p data-testid="venue-payout-viva-locked-hint" style={{ fontFamily: font, fontSize: 12, color: 'var(--status-success-text)', margin: '6px 0 0', lineHeight: 1.4, fontWeight: 600 }}>
                Locked while IBAN is connected. Disconnect IBAN first to save €{SEPA_FEE.toFixed(2)} on every guest payment.
              </p>
            )}
          </div>
          <ChevronRight size={20} color={method === 'iban' ? 'var(--text-3)' : 'var(--text-2)'} />
        </div>
        {method === 'viva_wallet' && vivaIbanMasked && (
          <p data-testid="venue-payout-viva-iban-masked" style={{ fontFamily: font, fontSize: 13, color: 'var(--text-3)', margin: '10px 0 0', paddingLeft: 56 }}>
            {vivaIbanMasked}
          </p>
        )}
      </button>

      {/* Option 2: IBAN */}
      <button
        data-testid="venue-payout-option-iban"
        onClick={() => {
          if (method === 'viva_wallet') {
            toast.message('Disconnect Viva Wallet first to switch to IBAN.', { icon: 'i' });
            return;
          }
          navigate('/venue/settings/payouts/connect-iban', { state: { previousMethod: method } });
        }}
        disabled={method === 'viva_wallet'}
        style={{
          ...cardStyle,
          marginTop: 12,
          padding: '16px 16px',
          textAlign: 'left',
          border: method === 'iban' ? '2px solid #2563EB' : '1px solid var(--card-border)',
          background: 'var(--surface)',
          cursor: method === 'viva_wallet' ? 'not-allowed' : 'pointer',
          opacity: method === 'viva_wallet' ? 0.78 : 1,
          position: 'relative',
        }}
      >
        {method === 'viva_wallet' && (
          <span data-testid="venue-payout-iban-switch-warning" style={{
            position: 'absolute', top: -10, right: 12, padding: '3px 10px',
            background: 'var(--status-warning-text)', color: '#FFF', fontFamily: font, fontSize: 11, fontWeight: 700,
            borderRadius: 999, letterSpacing: 0.3,
          }}>
            ADDS €{SEPA_FEE.toFixed(2)} / TRANSACTION
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'rgba(37, 99, 235, 0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Landmark size={24} color="#2563EB" strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <p style={{ fontFamily: font, fontSize: 19, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                Bank account (IBAN)
              </p>
              {method === 'iban' && (
                <CheckCircle2 size={18} color="#2563EB" data-testid="venue-payout-iban-active-tick" />
              )}
            </div>
            <p style={{ fontFamily: font, fontSize: 14, color: 'var(--status-warning-text)', margin: '2px 0 0', lineHeight: 1.2, fontWeight: 600 }}>
              SEPA · 1–2 business days · €{SEPA_FEE.toFixed(2)} fee per transaction
            </p>
            {method === 'viva_wallet' && (
              <p data-testid="venue-payout-iban-locked-hint" style={{ fontFamily: font, fontSize: 12, color: 'var(--status-warning-text)', margin: '6px 0 0', lineHeight: 1.4, fontWeight: 600 }}>
                Locked while Viva Wallet is connected. Switching would cost you €{SEPA_FEE.toFixed(2)} on every guest payment.
              </p>
            )}
          </div>
          <ChevronRight size={20} color={method === 'viva_wallet' ? 'var(--text-3)' : 'var(--text-2)'} />
        </div>
        {method === 'iban' && ibanMasked && (
          <p data-testid="venue-payout-iban-masked" style={{ fontFamily: font, fontSize: 13, color: 'var(--text-3)', margin: '10px 0 0', paddingLeft: 56 }}>
            {ibanMasked}
          </p>
        )}
      </button>

      {/* Comparison / why Viva */}
      <div data-testid="venue-payout-why-viva" style={{
        ...cardStyle, marginTop: 16, padding: '14px 16px',
        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.06) 0%, rgba(34, 197, 94, 0.02) 100%)',
        border: '1px solid rgba(34, 197, 94, 0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Sparkles size={18} color="#16A34A" />
          <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--status-success-text)', margin: 0 }}>
            Why Viva Wallet saves you money
          </p>
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, fontFamily: font, fontSize: 14, color: 'var(--text)', lineHeight: 1.45 }}>
          <li><strong>€0.00 fee</strong> per transaction (vs €{SEPA_FEE.toFixed(2)} on IBAN)</li>
          <li><strong>Instant</strong> payouts (vs 1–2 business days on SEPA)</li>
          <li>100 transactions/month on IBAN = <strong>€{(SEPA_FEE * 100).toFixed(0)} extra cost</strong></li>
        </ul>
      </div>

      {/* Security footer */}
      <div style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px' }}>
        <ShieldCheck size={20} color="#16A34A" style={{ flexShrink: 0 }} />
        <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-2)', margin: 0, lineHeight: 1.25 }}>
          Powered by Viva Wallet. Payouts are securely processed and encrypted.
        </p>
      </div>

      {method && !disconnectConfirmOpen && (
        <button
          data-testid="venue-payout-disconnect-button"
          onClick={() => setDisconnectConfirmOpen(true)}
          disabled={disconnecting}
          style={{
            marginTop: 18, background: 'none', border: 'none',
            color: 'var(--status-danger-text)', fontFamily: font, fontSize: 16,
            textDecoration: 'underline', cursor: 'pointer',
            opacity: disconnecting ? 0.5 : 1,
          }}
        >
          {disconnecting ? 'Disconnecting…' : 'Disconnect payout method'}
        </button>
      )}
      {method && disconnectConfirmOpen && (
        <div
          data-testid="venue-payout-disconnect-confirm"
          style={{
            marginTop: 18,
            width: 'calc(100% - 32px)',
            maxWidth: 360,
            padding: 16,
            borderRadius: 16,
            background: 'var(--surface)',
            border: '1px solid rgba(220, 38, 38, 0.25)',
            boxShadow: '0 10px 24px rgba(220, 38, 38, 0.10)',
            boxSizing: 'border-box',
          }}
        >
          <p style={{ margin: 0, fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
            Disconnect this payout method?
          </p>
          <p style={{ margin: '6px 0 0', fontFamily: font, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.45 }}>
            Your venue will be hidden from the guest map until you reconnect a payout method.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={disconnecting}
              data-testid="venue-payout-disconnect-confirm-yes"
              style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', background: '#DC2626', color: '#FFF', fontFamily: font, fontWeight: 700, fontSize: 14, cursor: disconnecting ? 'wait' : 'pointer' }}
            >
              {disconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
            </button>
            <button
              type="button"
              onClick={() => setDisconnectConfirmOpen(false)}
              data-testid="venue-payout-disconnect-confirm-no"
              style={{ flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid var(--card-border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: font, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Payout method change history (audit log) */}
      {auditEntries.length > 0 && (
        <div data-testid="venue-payout-audit-card" style={{
          ...cardStyle, marginTop: 18, padding: '14px 14px',
          background: 'var(--surface)', border: '1px solid var(--card-border)',
        }}>
          <button
            type="button"
            onClick={() => setHistoryExpanded((v) => !v)}
            data-testid="venue-payout-audit-toggle"
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Clock size={18} color="var(--accent-purple)" />
              <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                Payout history
              </p>
              <span style={{ padding: '2px 8px', borderRadius: 9999, background: 'var(--accent-surface-soft)', color: 'var(--accent-purple)', fontSize: 11, fontWeight: 700 }}>
                {auditEntries.length}
              </span>
            </div>
            <ChevronRight size={18} color="#999" style={{ transform: historyExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.18s' }} />
          </button>
          {historyExpanded && (
            <div data-testid="venue-payout-audit-list" style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {auditEntries.map((entry, idx) => {
                const ago = formatRelativeTime(entry.created_at);
                const methodLabel = (m) => m === 'viva_wallet' ? 'Viva Wallet' : m === 'iban' ? 'IBAN' : '';
                const eventTitle =
                  entry.event_type === 'connected' ? `Connected ${methodLabel(entry.to_method)}` :
                  entry.event_type === 'disconnected' ? `Disconnected ${methodLabel(entry.from_method)}` :
                  entry.event_type === 'switched' ? `Switched ${methodLabel(entry.from_method)} → ${methodLabel(entry.to_method)}` :
                  entry.event_type === 'details_updated' ? `Updated ${methodLabel(entry.to_method)} details` :
                  entry.event_type;
                const dotColor =
                  entry.event_type === 'connected' ? '#16A34A' :
                  entry.event_type === 'disconnected' ? '#DC2626' :
                  entry.event_type === 'switched' ? 'var(--status-warning-text)' :
                  dotColor = 'var(--accent-purple)';
                return (
                  <div key={entry.id || idx} data-testid={`venue-payout-audit-entry-${idx}`} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderTop: idx === 0 ? 'none' : '1px solid var(--hairline)' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: dotColor, marginTop: 6, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontFamily: font, fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
                        {eventTitle}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
                        {ago} · {entry.actor_role === 'admin' ? 'by Allure admin' : 'by you'}
                      </p>
                      {entry.iban_masked_snapshot ? (
                        <p style={{ margin: '4px 0 0', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--text-3)' }}>
                          {entry.iban_masked_snapshot}
                        </p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {history && history.rows && history.rows.length > 0 && (
        <div data-testid="venue-payout-history-card" style={{
          ...cardStyle, marginTop: 18, padding: '14px 14px 8px',
          background: 'var(--surface)', border: '1px solid var(--card-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Receipt size={18} color="var(--accent-purple)" />
            <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
              Recent payouts
            </p>
            <span style={{ flex: 1 }} />
            <span data-testid="venue-payout-history-summary" style={{ fontFamily: font, fontSize: 12, color: 'var(--text-3)' }}>
              {history.summary?.executed_count || 0} sent · €{(history.summary?.executed_total_eur || 0).toFixed(2)}
            </span>
          </div>
          {history.rows.map((row) => {
            const status = row.status || 'pending';
            const cfg = {
              executed: { color: 'var(--status-success-text)', bg: 'rgba(34,197,94,0.10)', label: 'Sent', Icon: CheckCircle2 },
              pending_funds: { color: 'var(--status-warning-text)', bg: 'var(--status-warning-surface)', label: 'Pending funds', Icon: Clock },
              failed: { color: 'var(--status-danger-text)', bg: 'rgba(220,38,38,0.10)', label: 'Failed', Icon: XCircle },
              skipped: { color: '#6B7280', bg: 'rgba(107,114,128,0.10)', label: 'Skipped', Icon: AlertTriangle },
              pending: { color: '#6B7280', bg: 'rgba(107,114,128,0.10)', label: 'Pending', Icon: Clock },
            }[status] || { color: '#6B7280', bg: 'rgba(107,114,128,0.10)', label: status, Icon: Clock };
            const PillIcon = cfg.Icon;
            const createdAt = row.created_at ? new Date(row.created_at) : null;
            const dateLabel = createdAt && !isNaN(createdAt)
              ? createdAt.toLocaleDateString('en-GB', { timeZone: 'Europe/Athens', day: '2-digit', month: 'short' }) +
                ' · ' + createdAt.toLocaleTimeString('en-GB', { timeZone: 'Europe/Athens', hour: '2-digit', minute: '2-digit' })
              : '—';
            return (
              <div
                key={row.id}
                data-testid={`venue-payout-history-row-${row.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 0', borderTop: '1px solid var(--hairline)',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontFamily: font, fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                    €{Number(row.amount_eur || 0).toFixed(2)}
                  </p>
                  <p style={{ fontFamily: font, fontSize: 12, color: 'var(--text-3)', margin: '2px 0 0', lineHeight: 1.2 }}>
                    {dateLabel}
                    {row.payout_method === 'iban' && ' · IBAN'}
                    {row.payout_method === 'viva_wallet' && ' · Viva'}
                  </p>
                </div>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '4px 10px', borderRadius: 999,
                  background: cfg.bg, color: cfg.color,
                  fontFamily: font, fontSize: 12, fontWeight: 700,
                }}>
                  <PillIcon size={12} strokeWidth={2.4} />
                  {cfg.label}
                </div>
              </div>
            );
          })}
          {history.summary?.pending_count > 0 && (
            <>
              <p data-testid="venue-payout-history-pending-note" style={{
                fontFamily: font, fontSize: 12, color: 'var(--status-warning-text)',
                margin: '8px 0 4px', lineHeight: 1.3,
              }}>
                {history.summary.pending_count} payout{history.summary.pending_count === 1 ? '' : 's'} waiting on wallet funds — they&apos;ll send automatically once your Viva balance covers them.
              </p>
              <button
                data-testid="venue-payout-history-retry-button"
                onClick={handleRetryPending}
                disabled={retrying}
                style={{
                  marginTop: 8,
                  width: '100%',
                  padding: '10px 14px',
                  border: '1px solid var(--card-border)',
                  borderRadius: 12,
                  background: retrying ? 'var(--accent-surface-soft)' : 'var(--surface)',
                  color: 'var(--accent-purple)',
                  fontFamily: font,
                  fontSize: 14,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  cursor: retrying ? 'wait' : 'pointer',
                  opacity: retrying ? 0.7 : 1,
                  transition: 'background-color 180ms ease, opacity 180ms ease',
                }}
              >
                <RefreshCw size={14} strokeWidth={2.4} className={retrying ? 'animate-spin' : ''} />
                {retrying ? 'Retrying…' : `Retry ${history.summary.pending_count} pending payout${history.summary.pending_count === 1 ? '' : 's'}`}
              </button>
            </>
          )}
        </div>
      )}

      <button
        data-testid="venue-payout-help-link"
        onClick={() => navigate('/venue/settings/payouts/viva-info')}
        style={{
          marginTop: 14, background: 'none', border: 'none',
          display: 'flex', alignItems: 'center', gap: 6,
          color: 'var(--accent-purple)', fontFamily: font, fontSize: 14, cursor: 'pointer',
        }}
      >
        <Info size={14} /> Learn more about how payouts work
      </button>
    </div>
  );
}
